import { Box } from '@mui/material'
import { FC, useEffect } from 'react'
import { userCareTeamV2 } from '@fireflyhealth/api'
import {
  AvailablePhysicianSlot,
  isServerVisitReason,
  useAvailablePhysiciansWithSlotsV2,
  VisitReason,
} from '../AppointmentService'
import SelectDate from './SelectDate'
import { useQuery } from '~/shared/hooks/query'
import PhysicianSlotPickerGroupsByDay from './PhysicianSlotsPickerGroupsByDay'
import { APPOINTMENT_TYPE_TO_ROLE } from '~/shared/constants/visitReasons'

type BookAppointmentByDayTabProps = {
  patientId: number
  personId?: number
  visitReason: VisitReason
  now: Date
  date: Date
  setDate: (value: Date) => void
  slot: AvailablePhysicianSlot | null
  setSlot: (value: AvailablePhysicianSlot) => void
  setIsLoading: (e: boolean) => void
  isLoading?: boolean
}

const BookAppointmentByDayTab: FC<BookAppointmentByDayTabProps> = ({
  patientId,
  visitReason,
  date,
  setDate,
  now,
  slot,
  setSlot,
  setIsLoading,
}) => {
  // A server visit reason means the slots response itself names the care-team
  // providers eligible for the type, so the care-team read and the client-side
  // role filter below only serve the legacy wizard.
  const serverDrivenReason = isServerVisitReason(visitReason)
  const careTeamQuery = useQuery(
    userCareTeamV2.listQueryKey({ patient_id: String(patientId) }),
    async () => {
      const response = await userCareTeamV2.list({ patient_id: String(patientId) })
      return response.data
    },
    { enabled: !serverDrivenReason },
  )
  const careTeam = careTeamQuery.data
  const careTeamProviderNames: string[] = []

  const query = useAvailablePhysiciansWithSlotsV2(patientId, visitReason, date)

  useEffect(() => {
    if (query.isPending) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [query.isPending, setIsLoading])

  if (
    query.isPending ||
    query.isError ||
    (!serverDrivenReason && (!careTeam || careTeamQuery.isError))
  ) {
    return null
  }

  if (serverDrivenReason) {
    for (const provider of query.data?.care_team_providers ?? []) {
      careTeamProviderNames.push(provider.name)
    }
  } else {
    // Filter only eligible providers for the appointment type selected
    const rolesEligibleForVisitReason = APPOINTMENT_TYPE_TO_ROLE[visitReason]
    for (const physician of careTeam ?? []) {
      if (
        physician.group != null &&
        rolesEligibleForVisitReason.includes(physician.group) &&
        physician.physician_name_with_title
      ) {
        careTeamProviderNames.push(physician.physician_name_with_title)
      }
    }
  }
  return (
    <>
      <Box>
        <SelectDate
          date={date}
          setDate={setDate}
          now={now}
          datesWithSlotAvailability={query.data.dates_with_slot_availability}
          bookingWindowInHours={query.data.booking_window_in_hours}
        />

        <PhysicianSlotPickerGroupsByDay
          setDate={setDate}
          slots={query.data.appointments}
          value={slot}
          onChange={setSlot}
          bookingWindowInHours={query.data.booking_window_in_hours}
          careTeamProviderNames={careTeamProviderNames}
        />
      </Box>
    </>
  )
}

export default BookAppointmentByDayTab
